import { Observable } from "rxjs";
import { ApiKeyOrGetter } from "./GeneralService";
import { GeneralService } from "./GeneralService";

export interface RouteTarget {
  type: "agent" | "action" | "llm" | "none";
  agent_id?: number;
  action_id?: number;
}

export interface RoutingRuleCondition {
  id?: string | number;
  target_type: "request" | "state";
  variable_path: string;
  operator: "equals" | "not_equals" | "gt" | "gte" | "lt" | "lte" | "like" | "matches" | "sem_match" | "sem_similarity" | "classify" | "not_classify" | "sentiment" | "is_null" | "is_not_null";
  value_text?: string;
  value_number?: number;
  value_bool?: boolean;
  comparison_op?: ">" | ">=" | "=" | "<=" | "<" | "IS" | "IS_NOT";
  threshold_number?: number;
  enabled?: boolean;
}

export interface RoutingRule {
  id?: string | number;
  label?: string;
  if: string;
  condition_expression_template?: string;
  conditions?: RoutingRuleCondition[];
  route: RouteTarget;
  enabled?: boolean;
}

export interface RoutingConfig {
  type: "llm" | "rules";
  default?: RouteTarget;
}

export interface RoutingConfigResponse {
  config: RoutingConfig;
  rules: RoutingRule[];
}

export class RoutingService {
  /**
   * Get routing configuration and rules for an agent
   */
  static getRoutingConfig(
    agentId: number,
    apiKey: ApiKeyOrGetter
  ): Observable<RoutingConfigResponse> {
    return GeneralService.get(
      `/agent/${agentId}/routing/config`,
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Save routing configuration for an agent (mode and default route only)
   */
  static saveRoutingConfig(
    agentId: number,
    config: RoutingConfig,
    apiKey: ApiKeyOrGetter
  ): Observable<RoutingConfigResponse> {
    return GeneralService.put(
      `/agent/${agentId}/routing/config`,
      JSON.stringify({ config }),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Create a single routing rule
   */
  static createRule(
    agentId: number,
    rule: RoutingRule,
    apiKey: ApiKeyOrGetter
  ): Observable<RoutingRule> {
    console.log("🔍 RoutingService - Sending to backend:", rule);
    return GeneralService.post(
      `/agent/${agentId}/routing/rules`,
      JSON.stringify(rule),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Update a single routing rule
   */
  static updateRule(
    agentId: number,
    ruleId: number,
    rule: RoutingRule,
    apiKey: ApiKeyOrGetter
  ): Observable<RoutingRule> {
    return GeneralService.put(
      `/agent/${agentId}/routing/rules/${ruleId}`,
      JSON.stringify(rule),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Delete a single routing rule
   */
  static deleteRule(
    agentId: number,
    ruleId: number,
    apiKey: ApiKeyOrGetter
  ): Observable<void> {
    return GeneralService.delete(
      `/agent/${agentId}/routing/rules/${ruleId}`,
      "",
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Reorder a single rule to a new position
   */
  static reorderRule(
    agentId: number,
    ruleId: number,
    newIndex: number,
    apiKey: ApiKeyOrGetter
  ): Observable<void> {
    return GeneralService.put(
      `/agent/${agentId}/routing/rules/${ruleId}/reorder`,
      JSON.stringify({ new_index: newIndex }),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Test a routing rule against provided input values
   */
  static testRule(
    agentId: number,
    ruleId: number,
    testInputs: Record<string, any>,
    apiKey: ApiKeyOrGetter,
    conditions?: Array<any>,
    template?: string
  ): Observable<{
    rule_passed: boolean;
    conditions: Array<{
      condition_index: number;
      passed: boolean;
      variable: string;
      operator: string;
      expected: any;
      actual: any;
      similarity?: number;
      threshold?: number;
      comparison_op?: string;
    }>;
  }> {
    const body: any = {
      test_inputs: testInputs
    };

    // Always include conditions from the builder if provided (even if empty array)
    // This ensures we test the current state, not what's saved in DB
    if (conditions !== undefined) {
      body.conditions = conditions;
    }

    // Include template if provided for proper evaluation
    if (template !== undefined) {
      body.condition_expression_template = template;
    }

    return GeneralService.post(
      `/agent/${agentId}/routing/rules/${ruleId}/test`,
      JSON.stringify(body),
      () => {},
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

}

